// ============================================================
// useFirestoreSync - Firestore real-time sync (composition layer).
//
// Composes domain-specific sub-hooks:
//   useCombatantSync, useNarrativeSync, usePlayerPresence,
//   usePermissions, useSharedData, useDefenceSync,
//   useCharacterSync, useTurnTrackerSync (plus the seat's two)
//
// Each sub-hook receives a multiplayer service object (mp) for
// dependency inversion - the multiplayer functions are imported
// here and injected, rather than hardcoded in each sub-hook.
// ============================================================

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Veil } from '../engine/roster/hidden';
import * as multiplayer from '../multiplayer';
import type { Tab, Target, SessionPlayer } from '../types';
import type { Combatant } from '../engine/roster/types';
import useCombatantSync from './useCombatantSync';
import useNarrativeSync from './useNarrativeSync';
import usePlayerPresence from './usePlayerPresence';
import usePermissions from './usePermissions';
import useSharedData from './useSharedData';
import useDefenceSync from './useDefenceSync';
import useCharacterSync from './useCharacterSync';
import useTurnTrackerSync from './useTurnTrackerSync';
import useDiagUpload from './useDiagUpload';
import useActingAs from './useActingAs';

interface UseFirestoreSyncParams {
  sessionId: string | null;
  isGm: boolean;
  playerId: string | null;
  /** The combatant this client acts as - published on the seat (v8.363.2). */
  actorId?: string | null;
  /**
   * The GM's veil over hidden figures (plan map-display, G1; `useVeil`): applied
   * where the shared log and the defence documents are written, and the reason
   * a hidden actor is never published on the seat. Absent: nothing is hidden.
   */
  veil?: MutableRefObject<Veil>;
  /** Firebase auth user (needs displayName/sessionName) */
  user: { uid?: string; sessionName?: string; displayName?: string | null };
  tabs: Tab[];
  setTabs: Dispatch<SetStateAction<Tab[]>>;
  /** v7.0.0 roster sync (replaces the targets collection). */
  roster: Combatant[];
  setRoster: (updater: (prev: Combatant[]) => Combatant[]) => void;
  /** false keeps the legacy targets/* writes alive during the transition. */
  syncCombatants?: boolean;
  /**
   * true moves roster READS to combatants/*. Still OFF by default.
   *
   * The write path, the migration and the convergence guards are all in
   * place and unit-tested, but the turn tracker's rapid multi-client undo
   * still loses steps when two listeners are live: a snapshot can land
   * between an undo's local restore and its batch write. Flipping this
   * needs a proper look at ordering there, and nothing in the UI work
   * depends on it - loadout sync between players is the only user-visible
   * gap, and it is already absent today.
   */
  rosterAuthoritative?: boolean;
  setActiveTab: Dispatch<SetStateAction<string | null>>;
  tgtTemplates: Record<string, Target>;
  setTgtTemplates: Dispatch<SetStateAction<Record<string, Target>>>;
  setSession: Dispatch<
    SetStateAction<{ id: string | null; code: string | null; isGm: boolean; playerId: string | null }>
  >;
  setConnectedPlayers: Dispatch<SetStateAction<SessionPlayer[]>>;
  /** Surface sync failures to the user (e.g. a toast); optional for tests */
  onSyncError?: (message: string) => void;
  /** A reaction ran out of time - resolve it as "no reaction". See useDefenceSync. */
  onDefenceExpire?: (combatantId: string, doc: Record<string, unknown>) => void;
}

/**
 * The legacy targets/* collection is GONE (v7.6.0). These names survive as
 * no-ops because they are threaded through a dozen call sites that have
 * nothing to do with syncing; the roster owns all of it now. Removing the
 * parameters is a separate, purely mechanical tidy-up. Fresh per render,
 * as they always were.
 */
function legacyTargetSurface() {
  return {
    syncTargetToFirestore: () => {},
    syncAllTabsToFirestore: () => {},
    setTabSourceCharacter: () => {},
    lastTgtJsonRef: { current: {} as Record<string, string> },
    tgtSyncTimerRef: { current: {} as Record<string, ReturnType<typeof setTimeout>> },
  };
}

/** The roster's sync, under the names the simulator reads. */
function rosterSurface(c: ReturnType<typeof useCombatantSync>) {
  const { syncCombatantState, syncCombatantSheet, syncAllCombatants, removeCombatant, forgetHeard } = c;
  return {
    syncCombatantState,
    syncCombatantSheet,
    syncAllCombatants,
    removeCombatant,
    forgetHeard,
    rosterHydrated: c.hydrated,
  };
}

/** The shared log's sync, including the refs undo clears. */
function narrativeSurface(n: ReturnType<typeof useNarrativeSync>) {
  const { syncNarrativeToFirestore, flushNarrativeQueue, firestoreNarrative, narrativeClearTime } = n;
  const { setNarrativeClearTime, narrativeQueueRef, narrativeFlushTimerRef } = n;
  return {
    ...{ syncNarrativeToFirestore, flushNarrativeQueue, firestoreNarrative, narrativeClearTime },
    ...{ setNarrativeClearTime, narrativeQueueRef, narrativeFlushTimerRef },
  };
}

/** The character library's sync; `synced` is exposed as `charactersSynced`. */
function characterSurface(c: ReturnType<typeof useCharacterSync>) {
  const { sessionCharacters, pushCharacter, pushAllCharacters, deleteSessionCharacter, pullCharacter } = c;
  return {
    sessionCharacters,
    charactersSynced: c.synced,
    pushCharacter,
    pushAllCharacters,
    deleteSessionCharacter,
    pullCharacter,
  };
}

/** The reaction prompts' sync. */
function defenceSurface(d: ReturnType<typeof useDefenceSync>) {
  const { createPendingDefence, clearPendingDefence, clearAllPendingDefences, clearPendingDefencesSince } = d;
  const { getDefenceForTab, isDefencePending, pendingDefences } = d;
  return {
    ...{ createPendingDefence, clearPendingDefence, clearAllPendingDefences, clearPendingDefencesSince },
    ...{ getDefenceForTab, isDefencePending, pendingDefences },
  };
}

/**
 * The seat's side-channels: who this client acts as - the vox seeds
 * groups from it (v8.363.2) - and its own last log lines, for the GM's
 * bug report (v8.362.0).
 */
function useSeat(p: UseFirestoreSyncParams, mp: typeof multiplayer) {
  const { sessionId, playerId, actorId } = p;
  // Every member reads the seat documents: a hidden actor is published as nobody (plan map-display, G1).
  const shown = actorId && !p.veil?.current.hides(actorId) ? actorId : null;
  useActingAs({ sessionId, playerId, actorId: shown, setPlayerProfile: mp.setPlayerProfile });
  // The GM's own lines are on the GM's screen already, and may name a hidden figure: only players upload.
  useDiagUpload({ sessionId, uid: p.isGm ? null : (p.user.uid ?? null), setSeatDiag: mp.setSeatDiag });
}

/** Firestore real-time sync: every domain sub-hook, composed, with the multiplayer service injected. */
export default function useFirestoreSync(p: UseFirestoreSyncParams) {
  const { sessionId, isGm, playerId, user, onSyncError } = p;
  const mp = multiplayer;
  const combatantSync = useCombatantSync({
    ...{ sessionId: p.syncCombatants === false ? null : sessionId, playerId, isGm },
    ...{ roster: p.roster, setRoster: p.setRoster, authoritative: p.rosterAuthoritative !== false, mp, onSyncError },
  });
  const narrativeSync = useNarrativeSync({ sessionId, user, mp, onSyncError, veil: p.veil });
  const { setSession, setConnectedPlayers } = p;
  const presence = usePlayerPresence({ sessionId, isGm, playerId, user, setSession, setConnectedPlayers, mp });
  useSeat(p, mp);
  const perms = usePermissions({ sessionId, isGm, playerId, mp });
  const { tgtTemplates, setTgtTemplates } = p;
  const undoLocked = perms.isLocked('undo');
  const sharedData = useSharedData({ sessionId, tgtTemplates, setTgtTemplates, mp, onSyncError, undoLocked });
  const defenceSync = useDefenceSync({
    sessionId,
    playerId,
    tabs: p.tabs,
    mp,
    onExpire: p.onDefenceExpire,
    veil: p.veil,
  });
  const characterSync = useCharacterSync({ sessionId, playerId, mp });
  const turnTrackerSync = useTurnTrackerSync({ sessionId, mp });
  return {
    ...rosterSurface(combatantSync),
    ...legacyTargetSurface(),
    ...narrativeSurface(narrativeSync),
    ...{ onlinePlayers: presence.onlinePlayers, permissions: perms.permissions, isLocked: perms.isLocked },
    ...sharedData,
    ...defenceSurface(defenceSync),
    ...characterSurface(characterSync),
    ...turnTrackerSync,
  };
}
